import { AuthState } from '../types/app';
import { ynabService } from './ynabService';

class AuthService {
  private readonly STORAGE_KEY = 'ynab_auth_state';

  /**
   * Get current authentication state from localStorage
   */
  getAuthState(): AuthState {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return this.getDefaultAuthState();
      }

      const authState: AuthState = JSON.parse(stored);
      
      // Check if token is expired
      if (authState.tokenExpiry && Date.now() > authState.tokenExpiry) {
        this.clearAuthState();
        return this.getDefaultAuthState();
      }

      return authState;
    } catch (error) {
      console.error('Error reading auth state:', error);
      this.clearAuthState();
      return this.getDefaultAuthState();
    }
  }

  /**
   * Save authentication state to localStorage
   */
  setAuthState(authState: AuthState): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(authState));
    } catch (error) {
      console.error('Error saving auth state:', error);
    }
  }

  /**
   * Clear authentication state
   */
  clearAuthState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing auth state:', error);
    }
  }

  /**
   * Initiate OAuth flow
   */
  initiateAuth(): void {
    const authUrl = ynabService.getAuthorizationUrl();
    window.location.href = authUrl;
  }

  /**
   * Handle OAuth callback and extract tokens
   */
  handleAuthCallback(hash: string): AuthState | null {
    const authResponse = ynabService.parseAuthCallback(hash);
    
    if (!authResponse) {
      return null;
    }

    const authState: AuthState = {
      isAuthenticated: true,
      accessToken: authResponse.access_token,
      tokenExpiry: Date.now() + (authResponse.expires_in * 1000),
      budgetId: null, // Will be set after budget selection
    };

    this.setAuthState(authState);
    return authState;
  }

  /**
   * Update budget ID in auth state
   */
  setBudgetId(budgetId: string): void {
    const currentState = this.getAuthState();
    if (currentState.isAuthenticated) {
      this.setAuthState({
        ...currentState,
        budgetId,
      });
    }
  }

  /**
   * Check if user is authenticated and token is valid
   */
  isAuthenticated(): boolean {
    const authState = this.getAuthState();
    return authState.isAuthenticated && 
           authState.accessToken !== null && 
           (authState.tokenExpiry === null || Date.now() < authState.tokenExpiry);
  }

  /**
   * Logout user
   */
  logout(): void {
    this.clearAuthState();
  }

  private getDefaultAuthState(): AuthState {
    return {
      isAuthenticated: false,
      accessToken: null,
      tokenExpiry: null,
      budgetId: null,
    };
  }
}

export const authService = new AuthService();
